// PERIOD CLOSE'S PIECES (UI rebuild batch 5). The month arithmetic, the words a close names and a failed check's one
// line are COPIED from screens/Periods.tsx (monthsSince, monthWords, amountList, groupFigures, gateParts, GateFailure)
// — the rules are today's; only where they are drawn changed. The old screen keeps its own copies until it is deleted
// at the end of the rebuild.
import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import type { PeriodGate, PeriodPreview, PeriodsResponse } from '../../../lib/api';
import { booksClock, displayAsset, fmtAmount, fmtMonth } from '../../../lib/format';
import { StateLine } from '../kit';
import { Pill } from '../../Pill';

/** Months from the regime start to the current one, newest first. Read off the books clock so "this month" flips at
 *  Dubai midnight, not UTC's 04:00 GST. (Periods.tsx monthsSince, verbatim.) */
export function monthsSince(regimeStart: string): string[] {
  const now = booksClock() ?? { y: 2026, m: 1 };
  const parts = regimeStart.split('-').map(Number);
  const ry = parts[0] ?? 2026;
  const rm = parts[1] ?? 1;
  const out: string[] = [];
  let y = now.y;
  let m = now.m;
  // guard the loop as well as the bound: a malformed regime_start must not spin
  while ((y > ry || (y === ry && m >= rm)) && out.length < 240) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  return out;
}

/** The month key the API speaks (YYYY-MM) in the operator's words — "September 2026". Mid-month noon, Dubai, so no
 *  zone edge can move it into the neighbouring month. */
export function monthWords(key: string): string {
  return fmtMonth(`${key}-15T12:00:00+04:00`);
}

/** "12,340.00 AED and 102,000 INR" — the amounts a close would post. */
export function amountList(assets: PeriodPreview['assets']): string {
  const words = assets.map((a) => `${fmtAmount(a.profit, a.asset)} ${displayAsset(a.asset)}`);
  if (words.length <= 1) return words[0] ?? 'nothing';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** What a month says. `open` is the RUNNING month; `ready` an ended month whose checks passed on a settled read;
 *  `blocked` a month with a failed check; `ended` an ended month whose checks are not (yet) known; `closed`; `unopened`. */
export type MonthState = 'open' | 'ended' | 'ready' | 'blocked' | 'closed' | 'unopened';

export const MONTH_WORD: Record<MonthState, string> = {
  open: 'Open', ended: 'To close', ready: 'Ready to close', blocked: 'Close blocked', closed: 'Closed', unopened: 'Not started',
};

export function MonthPill({ state }: { state: MonthState }) {
  const tone = state === 'closed' ? 'good' : state === 'ended' || state === 'ready' || state === 'blocked' ? 'warn' : 'neutral';
  return <Pill tone={tone}>{MONTH_WORD[state]}</Pill>;
}

/** A figure in the Worker's gate sentences is a bare integer in the asset's smallest unit: in thousands, with the
 *  screens' minus sign. (Periods.tsx groupFigures, verbatim.) */
function groupFigures(sentence: string): string {
  return sentence.replace(/(?<![\w.,]|\w-)([-−]?)([1-9]\d{3,})(?!\w|[.,-]\d)/g, (_m, sign: string, digits: string) =>
    `${sign ? '−' : ''}${digits.replace(/\B(?=(\d{3})+$)/g, ',')}`);
}

/** A gate sentence already in display units ("401,880,412.10") says its amounts by rule 10 like every screen — a dirham
 *  figure whole, half up ("401,880,412") — through the shared formatter (Astra, 1 Nov 2026). Only grouped amounts of a
 *  fiat asset are touched: a rate is never rounded, and a year or a count has no grouping comma. */
function displayFigures(sentence: string, asset: string | null): string {
  if (!asset || !['AED', 'INR', 'USD', 'EUR', 'GBP'].includes(asset)) return sentence;
  return sentence.replace(/(?<![\w.])-?\d{1,3}(?:,\d{3})+(?:\.\d+)?(?![\w.])/g, (m) => fmtAmount(m.replace(/,/g, ''), asset));
}

const LIST_GATES: ReadonlySet<string> = new Set(['offset_fully_matched']);

/** ONE FAILED CHECK, AS A LINE: its first clause, figures grouped, the asset in front; the rest behind Details.
 *  (Periods.tsx gateParts, verbatim.) */
function gateParts(g: PeriodGate): { asset: string | null; clause: string; rest: string[] } {
  const asset = g.name.includes(':') ? g.name.slice(g.name.lastIndexOf(':') + 1) : null;
  const [head = '', ...tail] = g.detail.split(' — ');
  const list = LIST_GATES.has(g.name) ? /^([^:]+):\s+(.+)$/.exec(head) : null;
  const figures = g.units === 'display' ? (t: string) => displayFigures(t, asset) : groupFigures;
  const clause = figures(list ? list[1]! : head);
  const sentence = (t: string) => {
    const c = figures(t.charAt(0).toUpperCase() + t.slice(1));
    return /[.!?]$/.test(c) ? c : `${c}.`;
  };
  return { asset, clause: clause.charAt(0).toUpperCase() + clause.slice(1), rest: [...(list ? [figures(list[2]!)] : []), ...tail.map(sentence)] };
}

/** What a two-figure check compares, in one word (the gate's kind, before the colon). */
const MISMATCH_WORD: Readonly<Record<string, string>> = { deals_spread_tie: 'Margin', memo_mirrors_offset: 'Margin memo', v4_identity: 'Income and expenses' };
const FIGURE = /-?\d{1,3}(?:,\d{3})*(?:\.\d+)?/;

/** A FAILED COMPARISON ("net trade margin A vs B from the deals — why") reads as its difference: "Margin mismatch ·
 *  4,211 AED" by rule 10, the two figures it compared — each with its unit — and the why behind Details (Astra, 1 Nov 2026:
 *  two large amounts with the asset in front said less than the gap between them). Display-unit gates of a fiat asset only. */
function mismatchParts(g: PeriodGate): { word: string; gap: string; more: string[] } | null {
  const asset = g.name.includes(':') ? g.name.slice(g.name.lastIndexOf(':') + 1) : null;
  if (g.units !== 'display' || !asset || !['AED', 'INR', 'USD', 'EUR', 'GBP'].includes(asset)) return null;
  const [head = '', ...tail] = g.detail.split(' — ');
  const [left, right] = head.split(' vs ');
  const a = left && FIGURE.exec(left), b = right && FIGURE.exec(right);
  if (!a || !b || head.split(' vs ').length !== 2) return null;
  const num = (m: string) => Number(m.replace(/,/g, ''));
  const unit = displayAsset(asset);
  // WHOLE UNITS BY RULE 10 (Astra, 1 Nov 2026), and the parts and the gap never disagree unexplained (the audit: 401,880,412 −
  // 401,876,202 shown = 4,210, stated 4,211 — each rounded apart): the gap is the true one, rounded; when the shown parts
  // subtract to another figure, Details says the exact gap.
  const x = num(a[0]), y = num(b[0]);
  const fig = (m: string) => `${fmtAmount(num(m), asset)} ${unit}`;
  const shownGap = Math.abs(Math.round(x) - Math.round(y)), trueGap = Math.abs(x - y);
  const exact = shownGap !== Math.round(trueGap) ? [`Exact gap ${fmtAmount(trueGap, asset, 2)} ${unit}; the figures above are rounded.`] : [];
  const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  const kind = g.name.slice(0, g.name.indexOf(':'));
  const word = MISMATCH_WORD[kind] ?? cap(left!.slice(0, a.index).trim());
  const after = right!.slice(b.index! + b[0].length).trim();
  return {
    word: `${word} mismatch`,
    gap: `${fmtAmount(trueGap, asset)} ${unit}`,
    more: [
      cap(left!.replace(a[0], fig(a[0])).trim()),
      after ? `${cap(after)}: ${fig(b[0])}` : `Compared with ${fig(b[0])}`,
      ...tail.map((t) => cap(/[.!?]$/.test(t) ? t : `${t}.`)),
      ...exact,
    ],
  };
}

export function GateFailure({ gate }: { gate: PeriodGate }) {
  const mm = mismatchParts(gate);
  if (mm) {
    return (
      <li>
        {/* The check's name left, its gap in its own seat right (one fact per place, 1 Nov 2026). */}
        <StateLine tone="warn" more={mm.more.map((t, i) => <p key={i}>{t}</p>)}>
          <span className="inline-flex flex-wrap items-baseline gap-x-4"><span>{mm.word}</span><span className="whitespace-nowrap font-mono tabular">{mm.gap}</span></span>
        </StateLine>
      </li>
    );
  }
  const { asset, clause, rest } = gateParts(gate);
  const more = [...rest, ...(asset && gate.units !== 'display' ? ['Figures are in the smallest unit of the asset.'] : [])];
  return (
    <li>
      <StateLine tone="warn" more={more.length > 0 ? more.map((t, i) => <p key={i}>{t}</p>) : undefined}>
        {asset ? `${displayAsset(asset)} · ` : ''}{clause}
      </StateLine>
    </li>
  );
}

const STEP_TONE = { done: 'bg-pos-bg text-pos', warn: 'bg-warn-bg text-warn', neutral: 'bg-panel-2 text-tg-hint' } as const;

/** One step of the close (Astra's numbered rows): its number, what, one muted line, and its state at the right; what
 *  the step holds (failed checks, the figures) beneath. */
export function Step({ n, title, sub, state, tone = 'neutral', children }: {
  n: number;
  title: string;
  sub?: ReactNode;
  state?: ReactNode;
  tone?: keyof typeof STEP_TONE;
  children?: ReactNode;
}) {
  return (
    <li className="border-b border-stroke-soft px-4 py-4 last:border-b-0 lg:px-5">
      <div className="grid grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-3">
        <span className={`grid h-7 w-7 place-items-center rounded-full font-mono tabular text-meta ${STEP_TONE[tone]}`} aria-hidden>{n}</span>
        <div className="min-w-0">
          <div className="text-body font-medium">{title}</div>
          {sub && <div className="mt-1 text-meta text-tg-hint">{sub}</div>}
        </div>
        {state && <div className="shrink-0">{state}</div>}
      </div>
      {children && <div className="mt-3 lg:pl-10">{children}</div>}
    </li>
  );
}

/** The figures a close posts: one line per asset — what reaches retained earnings — and, behind one tap, how each
 *  adds up (margin measured + already posted). Every figure of the old table is here; the parts are folded. */
export function Postings({ assets }: { assets: PeriodPreview['assets'] }) {
  const [parts, setParts] = useState(false);
  return (
    <div>
      <ul className="space-y-2">
        {assets.map((a) => (
          <li key={a.asset}>
            <div className="flex items-baseline justify-between gap-3 text-body">
              <span className="text-tg-hint">{displayAsset(a.asset)}</span>
              <span className="amt-key break-all text-right">{fmtAmount(a.profit, a.asset)}</span>
            </div>
            {parts && (
              <dl className="mt-1 space-y-1 border-l border-stroke pl-3 text-meta text-tg-hint">
                <div className="flex items-baseline justify-between gap-3"><dt>Margin measured</dt><dd className="amt break-all text-right">{fmtAmount(a.memo_margin, a.asset)}</dd></div>
                <div className="flex items-baseline justify-between gap-3"><dt>Already posted</dt><dd className="amt break-all text-right">{fmtAmount(a.real_pnl, a.asset)}</dd></div>
              </dl>
            )}
          </li>
        ))}
      </ul>
      <button type="button" aria-expanded={parts} onClick={() => setParts((v) => !v)} className="btn-link mt-2 text-meta max-lg:-my-3.5 max-lg:py-3.5">
        {parts ? 'Hide breakdown' : 'Breakdown'}
      </button>
    </div>
  );
}

/** PREVIOUS CLOSES — what each month's close posted into retained earnings, newest month first, behind one tap. */
export function PreviousCloses({ legs }: { legs: PeriodsResponse['legs'] }) {
  const [open, setOpen] = useState(false);
  if (legs.length === 0) return null;
  const rows = [...legs].sort((a, b) => b.period.localeCompare(a.period) || a.asset.localeCompare(b.asset));
  return (
    <div>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)}
        className="flex min-h-11 items-center gap-1.5 text-body text-tg-hint hover:text-tg-text lg:min-h-10">
        <ChevronRight className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />Previous closes
      </button>
      {open && (
        <ul className="mt-1">
          {rows.map((l) => (
            <li key={l.reference} className="flex items-baseline justify-between gap-3 border-b border-stroke-soft py-2 text-body last:border-b-0">
              <span className="min-w-0">{monthWords(l.period)} <span className="text-tg-hint">· {displayAsset(l.asset)}</span></span>
              <span className="amt break-all text-right">{fmtAmount(l.profit, l.asset)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
